import gql from 'graphql-tag';

import {RUN_QUEUE_ITEM_FRAGMENT} from '../../graphql/runQueue';

const JOB_INFO_FRAGMENT = gql`
  fragment JobInfo on ArtifactCollection {
    id
    name
    description
    createdAt
    artifacts {
      edges {
        node {
          id
          description
          labels
          versionIndex
          aliases {
            id
            alias
          }
          usedBy(first: 1, order: "-createdAt", filters: $filters) {
            totalCount
            edges {
              node {
                id
                name
                displayName
                state
                createdAt
              }
            }
          }
        }
      }
    }
  }
`;

export const DEFAULT_RESOURCE_CONFIG_FRAGMENT = gql`
  fragment DefaultResourceConfigFragment on DefaultResourceConfig {
    id
    createdAt
    updatedAt
    scope {
      __typename
      Type
      ID
    }
    resource
    config
    templateVariables {
      name
      description
      schema
    }
  }
`;

export const FETCH_ENTITY_JOBS = gql`
  query FetchEntityJobs($entityName: String!, $pattern: String) {
    entity(name: $entityName) {
      id
      name
      projects(first: 100, pattern: $pattern) {
        edges {
          node {
            id
            name
            artifactType(name: "job") {
              id
              artifactCollections {
                edges {
                  node {
                    id
                    name
                    description
                    artifacts {
                      edges {
                        node {
                          id
                          description
                          labels
                          versionIndex
                          aliases {
                            id
                            alias
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_PROJECT_JOBS_PARTIAL = gql`
  query FetchProjectJobsPartial(
    $entityName: String!
    $projectName: String!
    $limit: Int!
    $after: String
    $filters: JSONString
  ) {
    project(entityName: $entityName, name: $projectName) {
      id
      name
      internalId
      artifactType(name: "job") {
        id
        artifactCollections(first: $limit, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...JobInfo
            }
          }
        }
      }
    }
  }
  ${JOB_INFO_FRAGMENT}
`;

export const FETCH_PROJECT_INTERNAL_ID = gql`
  query FetchProjectInternalId($entityName: String!, $projectName: String!) {
    project(entityName: $entityName, name: $projectName) {
      id
      internalId
    }
  }
`;

export const FETCH_PROJECT_JOBS = gql`
  query FetchProjectJobs($entityName: String!, $projectName: String!, $filters: JSONString) {
    project(entityName: $entityName, name: $projectName) {
      id
      name
      artifactType(name: "job") {
        id
        artifactCollections {
          edges {
            node {
              ...JobInfo
            }
          }
        }
      }
    }
  }
  ${JOB_INFO_FRAGMENT}
`;

export const FETCH_RECENTLY_USED_JOBS = gql`
  query FetchRecentlyUsedJobs($entityName: String!, $projectName: String!) {
    project(entityName: $entityName, name: $projectName) {
      id
      runs(first: 100) {
        edges {
          node {
            id
            createdAt
            job {
              id
              artifactSequence {
                id
                name
                state
                project {
                  id
                  name
                  entityName
                }
                description
                createdAt
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_JOB_VERSIONS = gql`
  query FetchJobVersions(
    $entityName: String!
    $projectName: String!
    $jobName: String!
  ) {
    project(entityName: $entityName, name: $projectName) {
      id
      artifactCollection(name: $jobName) {
        id
        description
        artifacts {
          edges {
            node {
              id
              createdAt
              createdBy {
                __typename
                ... on User {
                  id
                  name
                  username
                  photoUrl
                }
                ... on Run {
                  id
                  name
                  displayName
                  defaultColorIndex
                }
              }
              aliases {
                id
                alias
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_RECENTLY_USED_JOB_PARTIAL = gql`
  query FetchRecentlyUsedJobDetail(
    $entityName: String!,
    $projectName: String!,
    $limit: Int!,
    $after: String,
    $filters: JSONString,
    $artifactCollectionFilters: JSONString
  ) {
    project(entityName: $entityName, name: $projectName) {
      id
      name
      internalId
      entityName
      artifactType(name: "job") {
        id
        artifactCollections(first: $limit, after: $after, filters: $artifactCollectionFilters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              project {
                id
                name
                entityName
              }
              ...JobInfo
            }
          }
        }
      }
    }
    ${JOB_INFO_FRAGMENT}
  }
`;

export const FETCH_JOB = gql`
  query FetchJob($jobId: ID!) {
    artifactCollection(id: $jobId) {
      id
      name
    }
  }
`;

export const FETCH_JOB_DETAILS = gql`
  query FetchJobDetails($jobId: ID!, $usedByFilters: JSONString, $usedByFirst: Int) {
    artifactCollection(id: $jobId) {
      id
      name
      description
      createdAt
      project {
        id
        access
        name
        entity {
          id
          name
        }
      }
      artifacts {
        edges {
          node {
            id
            description
            labels
            versionIndex
            aliases {
              id
              alias
            }
            createdAt
            createdBy {
              __typename
              ... on User {
                id
                name
                username
                photoUrl
              }
              ... on Run {
                id
                name
                displayName
                defaultColorIndex
              }
            }
            usedBy(filters: $usedByFilters, first: $usedByFirst) {
              totalCount
              edges {
                node {
                  id
                  name
                  displayName
                  state
                  stopped
                  createdAt
                  user {
                    id
                    name
                    username
                    photoUrl
                  }
                  config
                  project {
                    id
                    name
                    entity {
                      id
                      name
                    }
                  }
                  defaultColorIndex
                  runInfo {
                    args
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_PROJECT_RUNS = gql`
  query FetchProjectRuns($entityName: String!, $projectName: String!) {
    project(entityName: $entityName, name: $projectName) {
      id
      runs {
        edges {
          node {
            id
            name
            displayName
            runInfo {
              git {
                remote
                commit
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_PROJECT_LAUNCH_QUEUES = gql`
  query FetchProjectLaunchQueues($entityName: String!, $projectName: String!) {
    project(entityName: $entityName, name: $projectName) {
      id
      name
      entity {
        id
        isTeam
        name
      }
      runQueues {
        id
        name
        access
        defaultResourceConfigID
        prioritizationMode
      }
    }
  }
`;

export const ENTITY_PROJECTS_QUERY = gql`
  query FetchEntityProjects($entityName: String!) {
    entity(name: $entityName) {
      id
      projects(first: 100, order: "-createdAt") {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  }
`;

export const ENTITY_PHOTO = gql`
  query EntityPhoto($entityName: String!) {
    entity(name: $entityName) {
      id
      photoUrl
    }
  }
`;

export const USER_ENTITIES = gql`
  query UserEntities {
    viewer {
      id
      teams {
        edges {
          node {
            id
            name
            photoUrl
            members {
              id
              username
              role
            }
          }
        }
      }
    }
  }
`;

export const VIEWER_DESTINATION_PROJECTS = gql`
  query ViewerDestinationProjects {
    viewer {
      id
      teams {
        edges {
          node {
            id
            name
            projects {
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const QUEUE_NAMES = gql`
  query QueueNames($entityName: String) {
    project(name: "model-registry", entityName: $entityName) {
      id
      runQueues {
        id
        name
      }
    }
  }
`;

export const ALL_QUEUES = gql`
  query AllQueues {
    viewer {
      id
      entity
      teams {
        edges {
          node {
            id
            name
            organizationId
            launchProject {
              id
              name
              launchAgents {
                id
                name
                createdAt
                heartbeatAt
                agentStatus
                stopPolling
                runQueues
                hostname
              }
              runQueues {
                id
                createdAt
                createdBy
                updatedAt
                name
                access
                prioritizationMode
                executorInfo
                externalLinks
                defaultResourceConfig {
                  id
                  ...DefaultResourceConfigFragment
                }
                runQueueItems(first: 500) {
                  edges {
                    node {
                      id
                      ...RunQueueItemFragment
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  ${RUN_QUEUE_ITEM_FRAGMENT}
  ${DEFAULT_RESOURCE_CONFIG_FRAGMENT}
`;

export const PUBLIC_QUEUE = gql`
  query PublicQueue($entityName: String, $publicQueueName: String) {
    entity(name: $entityName) {
      id
      name
      launchProject {
        id
        name
        launchAgents {
          id
          name
          createdAt
          heartbeatAt
          agentStatus
          stopPolling
          runQueues
          hostname
        }
        runQueue(name: $publicQueueName) {
          id
          createdAt
          createdBy
          updatedAt
          name
          access
          prioritizationMode
          executorInfo
          externalLinks
          defaultResourceConfig {
            id
            ...DefaultResourceConfigFragment
          }
          runQueueItems(first: 500) {
            edges {
              node {
                id
                ...RunQueueItemFragment
                error {
                  message
                  stage
                  filePaths
                }
                warnings {
                  message
                  stage
                  filePaths
                }
              }
            }
          }
        }
      }
    }
  }

  ${RUN_QUEUE_ITEM_FRAGMENT}
  ${DEFAULT_RESOURCE_CONFIG_FRAGMENT}
`;

export const QUEUE_BY_ID_LITE = gql`
  query QueueByIDLite($entityName: String!, $queueId: ID!) {
    project(name: "model-registry", entityName: $entityName) {
      id
      name
      launchAgents {
        id
        name
        createdAt
        heartbeatAt
        agentStatus
        stopPolling
        runQueues
        hostname
      }
      runQueue(id: $queueId) {
        id
        createdAt
        name
        defaultResourceConfig {
          id
          createdAt
          resource
          config
          templateVariables {
            name
            description
            schema
          }
        }
        prioritizationMode
        executorInfo
        externalLinks
        runQueueItems(first: 500) {
          edges {
            node {
              id
              ...RunQueueItemFragment
            }
          }
        }
      }
    }
  }

  ${RUN_QUEUE_ITEM_FRAGMENT}
`;

export const PUBLIC_QUEUE_LITE = gql`
  query PublicQueueLite($entityName: String, $publicQueueName: String) {
    entity(name: $entityName) {
      id
      name
      launchProject {
        id
        name
        launchAgents {
          id
          name
          createdAt
          heartbeatAt
          agentStatus
          stopPolling
          runQueues
          hostname
        }
        runQueue(name: $publicQueueName) {
          id
          createdAt
          createdBy
          updatedAt
          name
          access
          prioritizationMode
          executorInfo
          externalLinks
          defaultResourceConfig {
            id
            ...DefaultResourceConfigFragment
          }
          runQueueItems(first: 500) {
            edges {
              node {
                id
                ...RunQueueItemFragment
              }
            }
          }
        }
      }
    }
  }

  ${RUN_QUEUE_ITEM_FRAGMENT}
  ${DEFAULT_RESOURCE_CONFIG_FRAGMENT}
`;

export const ENTITY_QUEUES_LITE = gql`
  query EntityQueuesLite($entityName: String) {
    entity(name: $entityName) {
      id
      name
      launchProject {
        id
        name
        launchAgents {
          id
          name
          createdAt
          heartbeatAt
          agentStatus
          stopPolling
          runQueues
          hostname
        }
        runQueues {
          id
          createdAt
          createdBy
          updatedAt
          name
          access
          defaultResourceConfig {
            id
            createdAt
            updatedAt
            scope {
              __typename
              Type
              ID
            }
            resource
            config
            templateVariables {
              name
              description
              schema
            }
          }
          prioritizationMode
          executorInfo
          externalLinks
        }
      }
    }
  }
`;

export const ENITITY_QUEUE_BY_QUEUE_NAME_LITE = gql`
  query EntityQueueByQueueNameLite($entityName: String, $queueName: String) {
    entity(name: $entityName) {
      id
      name
      launchProject {
        id
        name
        launchAgents {
          id
          name
          createdAt
          heartbeatAt
          agentStatus
          stopPolling
          runQueues
          hostname
        }
        runQueue(name: $queueName) {
          id
          createdAt
          createdBy
          updatedAt
          name
          access
          defaultResourceConfig {
            id
            createdAt
            updatedAt
            scope {
              __typename
              Type
              ID
            }
            resource
            config
            templateVariables {
              name
              description
              schema
            }
          }
          prioritizationMode
          executorInfo
          externalLinks
        }
      }
    }
  }
`;

export const ALL_QUEUES_LITE = gql`
  query AllQueuesLite {
    viewer {
      id
      entity
      teams {
        edges {
          node {
            id
            name
            organizationId
            launchProject {
              id
              name
              launchAgents {
                id
                name
                createdAt
                heartbeatAt
                agentStatus
                stopPolling
                runQueues
                hostname
              }
              runQueues {
                id
                createdAt
                createdBy
                updatedAt
                name
                access
                prioritizationMode
                executorInfo
                externalLinks
                defaultResourceConfig {
                  id
                  createdAt
                  updatedAt
                  scope {
                    __typename
                    Type
                    ID
                  }
                  resource
                  config
                  templateVariables {
                    name
                    description
                    schema
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  ${RUN_QUEUE_ITEM_FRAGMENT}
`;

// Similar to RUNS_STATE_QUERY in src/graphql
export const RUNS_INFORMATION_QUERY = gql`
  query RunsInformation(
    $projectName: String!
    $entityName: String
    $filters: JSONString
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      runs(filters: $filters) {
        edges {
          node {
            id
            name
            displayName
            state
            createdAt
            heartbeatAt
          }
        }
      }
    }
  }
`;

export const FETCH_CODE_ARTIFACT_FILES = gql`
  query FetchCodeArtifactFiles($artifactID: ID!) {
    artifact(id: $artifactID) {
      id
      files {
        edges {
          node {
            id
            name
            displayName
            mimetype
            sizeBytes
            url
          }
        }
      }
    }
  }
`;

export const FETCH_AGENT_DETAILS = gql`
  query FetchAgentDetails($agentId: ID!) {
    launchAgent(id: $agentId) {
      id
      name
      createdAt
      updatedAt
      heartbeatAt
      runQueues
      hostname
      agentStatus
      stopPolling
      agentConfig
      version
    }
  }
`;

export const ENTITY_AGENTS = gql`
  query EntityAgents($entityName: String!) {
    entity(name: $entityName) {
      id
      name
      launchProject {
        id
        name
        launchAgents {
          id
          name
          createdAt
          heartbeatAt
          agentStatus
          stopPolling
          runQueues
          hostname
        }
      }
    }
  }
`;

export const FETCH_RUN_OUTPUT_LOG = gql`
  query FetchRunOutputLog(
    $entityName: String!
    $projectName: String!
    $runName: String!
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      run(name: $runName) {
        id
        logLineCount
        outputLogFile: files(first: 1, names: ["output.log"]) {
          edges {
            node {
              id
              name
              md5
              sizeBytes
              directUrl
              url(upload: false)
            }
          }
        }
      }
    }
  }
`;

export const QUEUE_BY_ID = gql`
  query QueueByID($entityName: String!, $queueId: ID!, $first: Int, $after: String) {
    project(name: "model-registry", entityName: $entityName) {
      id
      name
      launchAgents {
        id
        name
        createdAt
        heartbeatAt
        agentStatus
        stopPolling
        runQueues
        hostname
      }
      runQueue(id: $queueId) {
        id
        createdAt
        name
        defaultResourceConfig {
          id
          createdAt
          resource
          config
          templateVariables {
            name
            description
            schema
          }
        }
        prioritizationMode
        executorInfo
        externalLinks
        runQueueItems(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              ...RunQueueItemFragment
              error {
                message
                stage
                filePaths
              }
              warnings {
                message
                stage
                filePaths
              }
            }
          }
        }
      }
    }
  }

  ${RUN_QUEUE_ITEM_FRAGMENT}
`;

export const RUN_JOB_QUERY = gql`
  query RunJob($entityName: String!, $projectName: String!, $runName: String!) {
    project(name: $projectName, entityName: $entityName) {
      id
      run(name: $runName) {
        id
        job {
          id
          artifactSequence {
            id
            name
          }
          versionIndex
        }
      }
    }
  }
`;

export const RUN_FILES_WITH_FOLDER_AND_DIRECTURL = gql`
  query RunFilesAndFolderWithDirectURLs(
    $projectName: String!
    $entityName: String
    $runName: String!
    $maxNumFiles: Int!
    $pattern: String
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      name
      run(name: $runName) {
        id
        name
        projectId
        files(first: $maxNumFiles, pattern: $pattern) {
          edges {
            node {
              id
              name
              directUrl
              sizeBytes
              updatedAt
              artifact {
                id
                digest
              }
            }
          }
        }
      }
    }
  }
`;

export const ACCOUNT_USING_LAUNCH = gql`
  query AccountUsingLaunch($name: String!) {
    organization(name: $name) {
      id
      isLaunchActive
    }
  }
`;

export const ARTIFACT_PATH_ENTITY_PROJECTS = gql`
  query ArtifactPathEntityProjects($entityName: String!, $pattern: String) {
    entity(name: $entityName) {
      id
      name
      projects(first: 500, pattern: $pattern) {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  }
`;

export const ARTIFACT_PATH_SEARCH_ARTIFACTS = gql`
  query ArtifactPathSearchArtifacts(
    $entityName: String!
    $projectName: String!
    $limit: Int!
    $searchTerm: String
    $artifactsLimit: Int
  ) {
    entity(name: $entityName) {
      id
      project(name: $projectName) {
        id
        artifactCollectionsSearch(
          limit: $limit
          searchTerm: $searchTerm
          artifactsLimit: $artifactsLimit
        ) {
      edges {
        node {
          id
          name
          artifacts {
            edges {
              node {
                id
                versionIndex
                aliases {
                  id
                  alias
                }
              }
            }
          }
        }
      }
        }
      }
    }
  }
`;

export const ADD_PUBLIC_QUEUE_LAUNCH_AGENT = gql`
  mutation AddPublicQueueLaunchAgentTeamConfig(
    $input: AddPublicQueueLaunchAgentTeamConfigInput!
  ) {
    addPublicQueueLaunchAgentTeamConfig(input: $input) {
      success
      clientMutationID
    }
  }
`;

export const DELETE_PUBLIC_QUEUE_LAUNCH_AGENT = gql`
  mutation DeletePublicQueueLaunchAgentTeamConfig(
    $input: DeletePublicQueueLaunchAgentTeamConfigInput!
  ) {
    deletePublicQueueLaunchAgentTeamConfig(input: $input) {
      success
      clientMutationID
    }
  }
`;

export const VIEWER_TEAMS_PROJECTS = gql`
  query ViewerTeamsProjects($pattern: String) {
    viewer {
      id
      teams {
        edges {
          node {
            id
            name
            projects(first:100, pattern: $pattern) {
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_PROJECT_RUNS_FOR_CONFIG = gql`
  query FetchProjectRunsForConfig(
    $entityName: String!
    $projectName: String!
    $first: Int
  ) {
    project(entityName: $entityName, name: $projectName) {
      id
      name
      runs(first: $first, order: "-createdAt") {
        edges {
          node {
            id
            name
            displayName
            createdAt
            state
            defaultColorIndex
            config
            runInfo {
              args
            }
            user {
              id
              name
              username
              photoUrl
            }
            job {
              id
              description
              labels
              versionIndex
              aliases {
                id
                alias
              }
              artifactSequence {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;
